import { useMemo } from 'react';
import MessageItem from './MessageItem';

const SUGGESTION_COUNT = 4;
const QUICK_QUESTION_ICONS = [
  '📚', // Can I take company books home?
  '🧾', // Receipt / expense submission
  '🏠', // Remote work conditions
  '📝', // Telework application
  '💻', // Available software
  '🔐', // Last person leaving office
  '🎧', // Personal Bluetooth devices
  '💴', // Position allowance
  '📱', // Salesforce MFA / smartphone loss
  '📊', // Salesforce work records / meetings
];

function createSeededRandom(seed = '') {
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(index)) | 0;
  }

  return () => {
    hash = (hash * 1664525 + 1013904223) | 0;
    return (hash >>> 0) / 4294967296;
  };
}

function pickRandomQuestionIndexes(questions = [], seed = '') {
  const indexes = questions.map((_, index) => index);
  const random = createSeededRandom(seed || String(Date.now()));

  for (let index = indexes.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(random() * (index + 1));
    [indexes[index], indexes[randomIndex]] = [indexes[randomIndex], indexes[index]];
  }

  return indexes.slice(0, SUGGESTION_COUNT);
}

export default function MessageList({
  messages,
  t,
  language,
  suggestedQuestions = [],
  currentChatId,
  messagesEndRef,
  onFeedback,
  username,
  onSourceClick,
  onDocumentDownload,
  setInput,
}) {
  const suggestedQuestionIndexes = useMemo(
    () => pickRandomQuestionIndexes(suggestedQuestions, currentChatId),
    [currentChatId, suggestedQuestions]
  );

  if (messages.length === 0) {
    const visibleSuggestions = suggestedQuestionIndexes
      .map(index => ({
        text: suggestedQuestions[index],
        icon: QUICK_QUESTION_ICONS[index] || '💬',
      }))
      .filter(question => question.text);

    return (
      <div className="flex flex-col items-center justify-center flex-1 p-6 text-center" style={{ height: "calc(100vh - 140px)" }}>
        <h3 className="text-lg font-medium mb-2 text-light-text dark:text-primary">
          {t.welcome} {t.welcome2}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6 w-full max-w-xl">
          {visibleSuggestions.map(question => (
            <button
              key={question.text}
              type="button"
              onClick={() => setInput?.(question.text)}
              className="p-3 text-left rounded-xl border transition text-sm border-light-border bg-light-msg-bg hover:bg-light-hover text-light-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-thinking-primary dark:border-glass dark:bg-input dark:hover:bg-hover dark:text-primary"
            >
              {question.icon} {question.text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto min-h-0">
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-1">
        {messages.map((msg, idx) => (
          <MessageItem
            key={msg.id || idx}
            message={msg}
            messageIndex={idx}
            onFeedback={onFeedback}
            username={username}
            language={language}
            onSourceClick={onSourceClick}
            onDocumentDownload={onDocumentDownload}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
